import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"

import { api } from "@/services/api"

export interface Permission {
  id: string
  name: string
  description?: string
  resource_type?: string
  action: string
  is_system_permission?: boolean
}

export interface PermissionGroup {
  resource_type: string
  permissions: Permission[]
  icon: string
  color: string
}

function getResourceIcon(resourceType: string): string {
  const icons: Record<string, string> = {
    system: "⚙️",
    users: "👥",
    files: "📁",
    general: "🔧",
  }
  return icons[resourceType] || "🔧"
}

function getResourceColor(resourceType: string): string {
  const colors: Record<string, string> = {
    system: "red",
    users: "blue",
    files: "purple",
    general: "gray",
  }
  return colors[resourceType] || "gray"
}

export const useRBACPermissions = () => {
  const {
    data: permissions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["rbac-permissions"],
    queryFn: async () => {
      const response = await api.admin.listPermissionsApiV1AdminPermissionsGet()
      return response.data
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const permissionGroups = useMemo(() => {
    return (permissions || []).reduce(
      (groups: PermissionGroup[], permission) => {
        const resourceType = permission.resource_type || "general"
        let group = groups.find((g) => g.resource_type === resourceType)

        if (!group) {
          group = {
            resource_type: resourceType,
            permissions: [],
            icon: getResourceIcon(resourceType),
            color: getResourceColor(resourceType),
          }
          groups.push(group)
        }

        group.permissions.push({
          id: permission.id,
          name: permission.name,
          description: permission.description || undefined,
          resource_type: permission.resource_type || undefined,
          action: permission.action,
        })
        return groups
      },
      [],
    )
  }, [permissions])

  return { permissions, permissionGroups, isLoading, error }
}
